function getSingleOf(key: string, section: Element | null) {
  let value = null;

  if (section == null) {
    return value;
  }

  const element = section.querySelector(`[data-attribute=${key}]`);

  if (element !== null) {
    value = element.getAttribute('data-value');
  }

  return value;
}

function getMultipleOf(key: string, section: Element | null) {
  const list: string[] = [];

  if (section == null) {
    return list;
  }

  const results = section.querySelectorAll(`[data-attribute=${key}]`);

  if (results && results.length) {
    results.forEach((element) => {
      const value = element.getAttribute('data-value');

      if (value) {
        list.push(value);
      }
    });
  }

  return list;
}

/**
 *
 * Mark references as empty links for later recognition.
 */
export function formatReferences(article: string) {
  let formattedString = article;
  // Replace control character "" with right double quotation mark
  formattedString = formattedString.replace(//g, '”');

  // Add property "data-empty-link" to all self-closing links
  formattedString = formattedString.replace(
    /<a(?:(?!<\/a>).)*?\/>/gs,
    (match) => {
      return `${match.split('/>')[0]} data-empty-link="true">empty link</a>`;
    },
  );

  return formattedString;
}

/* Adds a data attribute 'chapter-id' to each element, which is the ID of the 
closest h1, h2, h3, h4, h5 or h6 element before each element. If the element 
itself is a heading element, its chapter-id will be its own ID. */
function formatArticleElements(elements: Element[]): HTMLElement[] {
  let chapterId = '';

  return elements.map((element) => {
    if (new RegExp(/H[1-6]/i).exec(element.tagName)) {
      if (element.textContent) {
        element.setAttribute('id', encodeURIComponent(element.textContent));
      }

      const id = element.getAttribute('id');

      if (id) {
        chapterId = id;
      }
    }

    // Now we set 'chapter-id' for all elements, not just non-heading ones
    if (chapterId) {
      element.setAttribute('data-chapter-id', chapterId);
    }

    return element as HTMLElement;
  });
}

interface FormattedContentData {
  article: Element;
  articleElements: HTMLElement[];
  tableOfContents: Element[];
  footnotes: Element[];
}

export function getArticleContentFromXML(
  article: string,
): FormattedContentData {
  let formattedArticleString = article;

  // Remove superscripts around references
  formattedArticleString = formatReferences(formattedArticleString);

  // Parse the xml to dom as html
  const parser = new DOMParser();
  const xmlDocument = parser.parseFromString(
    formattedArticleString,
    'text/xml',
  );

  // Go through all previously empty links and format them if they are footnotes
  Array.from(xmlDocument.querySelectorAll('a[data-empty-link="true"]')).forEach(
    (link, index) => {
      if (link.className.includes('footnote')) {
        link.textContent = `${(index + 1).toString()}`;
        link.removeAttribute('data-empty-link');
        link.classList.remove('footnote');
        link.classList.add('reference');

        const linkId = link.getAttribute('href')?.split('#footnote-')[1];
        link.setAttribute('id', 'reference-' + linkId);
      }
    },
  );

  // Update all links (excluding references/footnotes) in the article to open to new
  Array.from(xmlDocument.querySelectorAll('a.link')).forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });

  // Remove meta section
  const meta = xmlDocument.querySelector('meta');
  meta?.remove();

  // Extract the footnotes of the article, and remove them from the original dom
  const footnotes = Array.from(xmlDocument.getElementsByTagName('aside')).map(
    (footnote) => {
      const cloned = footnote.cloneNode(true);
      (footnote.parentNode as ParentNode).removeChild(footnote);
      return cloned as Element;
    },
  );

  // Extract the actual article (chapter) from the html
  const articleHTML = xmlDocument
    .querySelectorAll('chapter > div')
    .item(0) as Element;

  const formattedArticleElements = formatArticleElements(
    articleHTML ? Array.from(articleHTML.children) : [],
  );

  const tableOfContents = Array.from(
    xmlDocument.querySelectorAll('h1, h2, h3, h4, h5, h6'),
  );

  return {
    tableOfContents,
    article: articleHTML,
    articleElements: formattedArticleElements,
    footnotes,
  };
}

/**
 *
 * @param articleXML
 */
export function getArticleMetaFromXML(articleXML: string) {
  let formattedArticleString = articleXML;

  // Remove superscripts around references
  formattedArticleString = formatReferences(formattedArticleString);

  // Parse the xml to dom as html
  const parser = new DOMParser();
  const xmlDocument = parser.parseFromString(
    formattedArticleString,
    'text/xml',
  );

  // Extract metadata from article
  const metaSection = xmlDocument.querySelector('meta');

  const title = getSingleOf('title', metaSection);

  if (title === null) {
    throw new Error('Title missing from meta data');
  }

  // Get authors
  const authors: string[] = getMultipleOf('author', metaSection);

  // Get dates
  const releaseDate: string = getSingleOf('releaseDate', metaSection) ?? '';
  const publishDate: string = getSingleOf('publishDate', metaSection) ?? '';

  // Get primary category
  const primary = getSingleOf('categoryPrimary', metaSection);

  // Chech that primarycategory was found
  if (primary == null) {
    throw new Error('No primary category defined');
  }

  // Get secondary categories
  const secondary = getMultipleOf('categorySecondary', metaSection);

  // Get lead paragraph
  const description: string | undefined =
    getSingleOf('lead', metaSection) ?? undefined;

  // Get cover image
  const coverImage: string | undefined =
    getSingleOf('coverImage', metaSection) ?? undefined;

  // Get language
  const language = getSingleOf('language', metaSection) ?? undefined;

  // Get content type
  const type = getSingleOf('contentType', metaSection);

  // Get isPublic
  const isPublic = getSingleOf('isPublic', metaSection);

  // Get isPublic
  const upcoming = getSingleOf('upcoming', metaSection);

  // Get keywords string
  const keywords = getSingleOf('keywords', metaSection);

  // Get video playlist id
  const videoPlayListId: string | undefined =
    getSingleOf('videoplayerId', metaSection) ?? undefined;

  if (type === null) {
    throw new Error('Content type missing from meta data');
  }

  // Go through all previously empty links and format them if they are footnotes
  Array.from(xmlDocument.querySelectorAll('a[data-empty-link="true"]')).forEach(
    (link, index) => {
      if (link.className.includes('footnote')) {
        link.textContent = `${(index + 1).toString()}`;
        link.removeAttribute('data-empty-link');
        link.classList.remove('footnote');
        link.classList.add('reference');

        const linkId = link.getAttribute('href')?.split('#footnote-')[1];
        link.setAttribute('id', 'reference-' + linkId);
      }
    },
  );

  // Update all links (excluding references/footnotes) in the article to open to new
  Array.from(xmlDocument.querySelectorAll('a.link')).forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });

  // Remove meta section
  const meta = xmlDocument.querySelector('meta');
  meta?.remove();

  return {
    title,
    releaseDate,
    publishDate,
    authors,
    categories: {
      primary,
      secondary,
    },
    type,
    isPublic,
    language,
    keywords,
    upcoming,
    description,
    coverImage,
    videoPlayListId,
  };
}
